import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Boxes,
  CheckCircle2,
  ClipboardCheck,
  Plus,
  X,
  XCircle,
} from 'lucide-react';
import { type FormEvent, useMemo, useState } from 'react';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { StorePicker } from '../../components/StorePicker';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import type {
  Campaign,
  CampaignLocation,
  CampaignProduct,
  CurrentBAShelfStockPosition,
  CurrentPhysicalStockPosition,
  PaginatedResponse,
  PhysicalStockCount,
  PhysicalStockCountReviewDecision,
  StockReceipt,
  StoreProductAvailability,
} from '../../types/domain';

const dateTime = new Intl.DateTimeFormat('en-PK', {
  day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
  timeZone: 'Asia/Karachi',
});

type SortDirection = 'ascending' | 'descending' | 'none';
type BAShelfStockSortField = 'campaignAndStore' | 'product' | 'shelfStock' | 'observedAt' | 'brandAmbassador';
type OutOfStockSortField = 'store' | 'product' | 'markedOutOfStock' | 'reportedBy';

const baShelfStockOrderingFields: Record<BAShelfStockSortField, readonly string[]> = {
  campaignAndStore: [
    'observation__campaign_location__campaign__name',
    'observation__campaign_location__venue__name',
  ],
  product: ['product_name_snapshot'],
  shelfStock: ['quantity_units'],
  observedAt: ['observation__captured_at'],
  brandAmbassador: ['observation__ba_user__full_name'],
};

const outOfStockOrderingFields: Record<OutOfStockSortField, string> = {
  store: 'venue__name',
  product: 'product__name',
  markedOutOfStock: 'updated_at',
  reportedBy: 'changed_by__full_name',
};

function orderingValue(fields: readonly string[], direction: Exclude<SortDirection, 'none'>) {
  const prefix = direction === 'descending' ? '-' : '';
  return fields.map((field) => `${prefix}${field}`).join(',');
}

function localDateTimeValue() {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
}

export function StockPage() {
  const { accessToken, capabilities, isPlatformAdmin, selectedWorkspace } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [stockTab, setStockTab] = useState<'ba-shelf' | 'physical' | 'out-of-stock' | 'receipts'>('ba-shelf');
  const [search, setSearch] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [baShelfStockSort, setBAShelfStockSort] = useState<{
    field: BAShelfStockSortField;
    direction: Exclude<SortDirection, 'none'>;
  }>({ field: 'observedAt', direction: 'descending' });
  const [outOfStockSort, setOutOfStockSort] = useState<{
    field: OutOfStockSortField;
    direction: Exclude<SortDirection, 'none'>;
  }>({ field: 'markedOutOfStock', direction: 'descending' });
  const [open, setOpen] = useState(false);
  const [campaign, setCampaign] = useState('');
  const [location, setLocation] = useState('');
  const [campaignProduct, setCampaignProduct] = useState('');
  const [quantity, setQuantity] = useState('');
  const [receivedAt, setReceivedAt] = useState(localDateTimeValue);
  const [deliveryReference, setDeliveryReference] = useState('');
  const [note, setNote] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [selectedCount, setSelectedCount] = useState<PhysicalStockCount | null>(null);
  const [countDecision, setCountDecision] = useState<'approved' | 'rejected' | null>(null);
  const [countReviewNote, setCountReviewNote] = useState('');
  const [countReviewIdempotencyKey, setCountReviewIdempotencyKey] = useState('');
  const workspaceId = selectedWorkspace?.organizationId;
  const canReceive = capabilities.includes('stock:receive');

  const campaignParams = new URLSearchParams({ page_size: '100', ordering: '-starts_on' });
  if (workspaceId) campaignParams.set('workspace', workspaceId);
  const campaigns = useApiCollection<Campaign>(
    'stock-campaigns', `/campaigns/?${campaignParams.toString()}`, accessToken,
  );
  const locations = useApiCollection<CampaignLocation>(
    'stock-locations',
    `/locations/?page_size=100&ordering=name${campaign ? `&campaign=${campaign}` : ''}`,
    accessToken,
    Boolean(campaign),
  );
  const products = useApiCollection<CampaignProduct>(
    'stock-campaign-products',
    `/campaign-products/?page_size=100&ordering=product__name${campaign ? `&campaign=${campaign}` : ''}`,
    accessToken,
    Boolean(campaign),
  );

  const params = new URLSearchParams({ page: String(page), page_size: '25', ordering: '-received_at' });
  if (workspaceId) params.set('workspace', workspaceId);
  if (search.trim()) params.set('search', search.trim());
  if (campaignFilter) params.set('campaign', campaignFilter);
  const receipts = useQuery({
    queryKey: ['stock-receipts', params.toString(), accessToken],
    enabled: Boolean(accessToken && stockTab === 'receipts'),
    queryFn: () => apiRequest<PaginatedResponse<StockReceipt>>(
      `/stock/receipts/?${params.toString()}`,
      accessToken ? { accessToken } : {},
    ),
  });
  const countParams = new URLSearchParams({ page_size: '100', ordering: '-captured_at' });
  const positionParams = new URLSearchParams({ page_size: '100' });
  if (workspaceId) {
    countParams.set('workspace', workspaceId);
    positionParams.set('workspace', workspaceId);
  }
  if (campaignFilter) {
    countParams.set('campaign', campaignFilter);
    positionParams.set('campaign', campaignFilter);
  }
  const physicalCounts = useQuery({
    queryKey: ['physical-stock-counts', countParams.toString(), accessToken],
    enabled: Boolean(accessToken && stockTab === 'physical'),
    queryFn: () => apiRequest<PaginatedResponse<PhysicalStockCount>>(
      `/stock/counts/?${countParams.toString()}`,
      accessToken ? { accessToken } : {},
    ),
  });
  const physicalPositions = useQuery({
    queryKey: ['physical-stock-positions', positionParams.toString(), accessToken],
    enabled: Boolean(accessToken && stockTab === 'physical'),
    queryFn: () => apiRequest<PaginatedResponse<CurrentPhysicalStockPosition>>(
      `/stock/positions/?${positionParams.toString()}`,
      accessToken ? { accessToken } : {},
    ),
  });
  const availabilityParams = new URLSearchParams({
    page_size: '100',
    ordering: orderingValue(
      [outOfStockOrderingFields[outOfStockSort.field]],
      outOfStockSort.direction,
    ),
  });
  if (workspaceId) availabilityParams.set('workspace', workspaceId);
  if (search.trim()) availabilityParams.set('search', search.trim());
  if (campaignFilter) availabilityParams.set('campaign', campaignFilter);
  const outOfStock = useQuery({
    queryKey: ['store-product-availability', availabilityParams.toString(), accessToken],
    enabled: Boolean(accessToken && stockTab === 'out-of-stock'),
    queryFn: () => apiRequest<PaginatedResponse<StoreProductAvailability>>(
      `/stock/availability/?${availabilityParams.toString()}`,
      accessToken ? { accessToken } : {},
    ),
  });
  const shelfStockParams = new URLSearchParams({
    page_size: '100',
    ordering: orderingValue(
      baShelfStockOrderingFields[baShelfStockSort.field],
      baShelfStockSort.direction,
    ),
  });
  if (workspaceId) shelfStockParams.set('workspace', workspaceId);
  if (search.trim()) shelfStockParams.set('search', search.trim());
  if (campaignFilter) shelfStockParams.set('campaign', campaignFilter);
  const baShelfStock = useQuery({
    queryKey: ['ba-shelf-stock-positions', shelfStockParams.toString(), accessToken],
    enabled: Boolean(accessToken && stockTab === 'ba-shelf'),
    queryFn: () => apiRequest<PaginatedResponse<CurrentBAShelfStockPosition>>(
      `/stock/shelf-observations/?${shelfStockParams.toString()}`,
      accessToken ? { accessToken } : {},
    ),
  });

  const campaignLocations = (locations.data?.results ?? []).filter(
    (location) => location.site_type === 'store',
  );
  const campaignProducts = products.data?.results.filter((item) => item.product_is_active) ?? [];
  const selectedCampaign = useMemo(
    () => campaigns.data?.results.find((item) => item.id === campaign),
    [campaigns.data?.results, campaign],
  );
  const receivableCampaigns = useMemo(() => {
    const visibleCampaigns = campaigns.data?.results ?? [];
    if (isPlatformAdmin) return visibleCampaigns;
    if (!workspaceId) return [];
    if (selectedWorkspace?.type === 'agency') {
      return visibleCampaigns.filter((item) => item.operating_agency === workspaceId);
    }
    if (selectedWorkspace?.type === 'client') {
      return visibleCampaigns.filter(
        (item) => item.client === workspaceId && item.operating_agency === null,
      );
    }
    return [];
  }, [campaigns.data?.results, isPlatformAdmin, selectedWorkspace?.type, workspaceId]);

  const resetForm = () => {
    setOpen(false); setCampaign(''); setLocation(''); setCampaignProduct(''); setQuantity('');
    setReceivedAt(localDateTimeValue()); setDeliveryReference(''); setNote('');
    setIdempotencyKey(crypto.randomUUID());
  };
  const mutation = useMutation({
    mutationFn: () => apiRequest<StockReceipt>('/stock/receipts/', {
      ...(accessToken ? { accessToken } : {}),
      method: 'POST',
      body: JSON.stringify({
        campaign_location: location,
        campaign_product: campaignProduct,
        quantity_received: Number(quantity),
        received_at: new Date(receivedAt).toISOString(),
        delivery_reference: deliveryReference,
        note,
        workspace: workspaceId ?? null,
        idempotency_key: idempotencyKey,
      }),
    }),
    onSuccess: async () => {
      resetForm();
      await queryClient.invalidateQueries({ queryKey: ['stock-receipts'] });
    },
  });
  const countReviewMutation = useMutation({
    mutationFn: () => apiRequest<PhysicalStockCountReviewDecision>(
      `/stock/counts/${selectedCount?.id}/review/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: 'POST',
        body: JSON.stringify({
          decision: countDecision,
          note: countReviewNote,
          workspace: workspaceId ?? null,
          idempotency_key: countReviewIdempotencyKey,
        }),
      },
    ),
    onSuccess: async () => {
      setSelectedCount(null);
      setCountDecision(null);
      setCountReviewNote('');
      setCountReviewIdempotencyKey('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['physical-stock-counts'] }),
        queryClient.invalidateQueries({ queryKey: ['physical-stock-positions'] }),
      ]);
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };
  const rows = receipts.data?.results ?? [];
  const countRows = physicalCounts.data?.results ?? [];
  const positionRows = physicalPositions.data?.results ?? [];
  const outOfStockRows = outOfStock.data?.results ?? [];
  const baShelfStockRows = baShelfStock.data?.results ?? [];
  const closeCountReview = () => {
    setSelectedCount(null);
    setCountDecision(null);
    setCountReviewNote('');
    setCountReviewIdempotencyKey('');
  };
  const selectCountDecision = (decision: 'approved' | 'rejected') => {
    setCountDecision(decision);
    setCountReviewIdempotencyKey(crypto.randomUUID());
  };
  const toggleBAShelfStockOrdering = (field: BAShelfStockSortField) => {
    setBAShelfStockSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'ascending'
        ? 'descending'
        : 'ascending',
    }));
  };
  const toggleOutOfStockOrdering = (field: OutOfStockSortField) => {
    setOutOfStockSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'ascending'
        ? 'descending'
        : 'ascending',
    }));
  };
  const baShelfStockSortDirection = (field: BAShelfStockSortField): SortDirection => (
    baShelfStockSort.field === field ? baShelfStockSort.direction : 'none'
  );
  const outOfStockSortDirection = (field: OutOfStockSortField): SortDirection => (
    outOfStockSort.field === field ? outOfStockSort.direction : 'none'
  );
  const sortIcon = (direction: SortDirection) => {
    if (direction === 'ascending') return <ArrowUp aria-hidden="true" size={14} />;
    if (direction === 'descending') return <ArrowDown aria-hidden="true" size={14} />;
    return <ArrowUpDown aria-hidden="true" size={14} />;
  };

  return <>
    <header className="page-heading">
      <div><p className="eyebrow">{selectedWorkspace?.name}</p><h1>Stock</h1><p>Monitor BA shelf stock, review supervisor physical counts, and preserve inbound delivery history for each campaign store.</p></div>
      {canReceive ? <div className="page-heading-actions"><button className="button primary" onClick={() => setOpen(true)} type="button"><Plus size={16} /> Receive stock</button></div> : null}
    </header>
    <section className="content-card stock-filters">
      <label className="form-field">Search<input aria-label="Search stock" onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="Campaign, store, product or delivery reference" value={search} /></label>
      <label className="form-field">Campaign<select aria-label="Filter stock by campaign" onChange={(event) => { setCampaignFilter(event.target.value); setPage(1); }} value={campaignFilter}><option value="">All campaigns</option>{campaigns.data?.results.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
    </section>
    <nav aria-label="Stock sections" className="stock-tabs">
      <button aria-pressed={stockTab === 'ba-shelf'} className={stockTab === 'ba-shelf' ? 'active' : ''} onClick={() => setStockTab('ba-shelf')} type="button">BA shelf stock</button>
      <button aria-pressed={stockTab === 'physical'} className={stockTab === 'physical' ? 'active' : ''} onClick={() => setStockTab('physical')} type="button">Supervisor physical stock</button>
      <button aria-pressed={stockTab === 'out-of-stock'} className={stockTab === 'out-of-stock' ? 'active' : ''} onClick={() => setStockTab('out-of-stock')} type="button">Out of stock</button>
      <button aria-pressed={stockTab === 'receipts'} className={stockTab === 'receipts' ? 'active' : ''} onClick={() => setStockTab('receipts')} type="button">Inbound receipts</button>
    </nav>
    {stockTab === 'ba-shelf' ? <>
      <div className="section-heading"><div><h2>Current BA shelf stock</h2><p>Latest sellable shelf quantity reported by a BA for each campaign product and store. It is a field signal, not a supervisor-verified physical count.</p></div><span>{baShelfStock.data?.count ?? 0}</span></div>
      <DataPanel loading={baShelfStock.isLoading} error={baShelfStock.isError} empty={baShelfStockRows.length === 0}>
        <section className="content-card data-card"><div className="data-table-wrap"><table className="data-table">
          <thead><tr>
            <th aria-sort={baShelfStockSortDirection('campaignAndStore')}><button aria-label="Sort BA shelf stock by campaign and store" className="table-sort-button" onClick={() => toggleBAShelfStockOrdering('campaignAndStore')} type="button">Campaign and store {sortIcon(baShelfStockSortDirection('campaignAndStore'))}</button></th>
            <th aria-sort={baShelfStockSortDirection('product')}><button aria-label="Sort BA shelf stock by product" className="table-sort-button" onClick={() => toggleBAShelfStockOrdering('product')} type="button">Product {sortIcon(baShelfStockSortDirection('product'))}</button></th>
            <th aria-sort={baShelfStockSortDirection('shelfStock')}><button aria-label="Sort BA shelf stock by shelf stock" className="table-sort-button" onClick={() => toggleBAShelfStockOrdering('shelfStock')} type="button">Shelf stock {sortIcon(baShelfStockSortDirection('shelfStock'))}</button></th>
            <th aria-sort={baShelfStockSortDirection('observedAt')}><button aria-label="Sort BA shelf stock by observed at" className="table-sort-button" onClick={() => toggleBAShelfStockOrdering('observedAt')} type="button">Observed at {sortIcon(baShelfStockSortDirection('observedAt'))}</button></th>
            <th aria-sort={baShelfStockSortDirection('brandAmbassador')}><button aria-label="Sort BA shelf stock by brand ambassador" className="table-sort-button" onClick={() => toggleBAShelfStockOrdering('brandAmbassador')} type="button">Brand ambassador {sortIcon(baShelfStockSortDirection('brandAmbassador'))}</button></th>
          </tr></thead>
          <tbody>{baShelfStockRows.map((row) => <tr key={`${row.campaign_location}-${row.campaign_product}`}>
            <td><strong>{row.venue_name}</strong><small>{row.campaign_name} · {row.venue_city}</small></td>
            <td><strong>{row.product_name_snapshot}</strong><small>{row.product_sku_snapshot || 'No SKU'}</small></td>
            <td><strong>{row.quantity_units.toLocaleString()} sellable units</strong><small>{Number(row.normalized_quantity).toLocaleString()} {row.normalized_unit}</small></td>
            <td><strong>{dateTime.format(new Date(row.captured_at))}</strong><small>{row.observation_reference}</small></td>
            <td><strong>{row.brand_ambassador_name}</strong></td>
          </tr>)}</tbody>
        </table></div></section>
      </DataPanel>
    </> : null}
    {stockTab === 'out-of-stock' ? <>
      <div className="section-heading"><div><h2>Out-of-stock products</h2><p>Live field status shared by every BA at the same store. Restocking is recorded by a checked-in BA in the mobile app.</p></div><span>{outOfStock.data?.count ?? 0}</span></div>
      <DataPanel loading={outOfStock.isLoading} error={outOfStock.isError} empty={outOfStockRows.length === 0}>
        <section className="content-card data-card"><div className="data-table-wrap"><table className="data-table">
          <thead><tr>
            <th aria-sort={outOfStockSortDirection('store')}><button aria-label="Sort out-of-stock products by store" className="table-sort-button" onClick={() => toggleOutOfStockOrdering('store')} type="button">Store {sortIcon(outOfStockSortDirection('store'))}</button></th>
            <th aria-sort={outOfStockSortDirection('product')}><button aria-label="Sort out-of-stock products by product" className="table-sort-button" onClick={() => toggleOutOfStockOrdering('product')} type="button">Product {sortIcon(outOfStockSortDirection('product'))}</button></th>
            <th aria-sort={outOfStockSortDirection('markedOutOfStock')}><button aria-label="Sort out-of-stock products by marked out of stock" className="table-sort-button" onClick={() => toggleOutOfStockOrdering('markedOutOfStock')} type="button">Marked out of stock {sortIcon(outOfStockSortDirection('markedOutOfStock'))}</button></th>
            <th aria-sort={outOfStockSortDirection('reportedBy')}><button aria-label="Sort out-of-stock products by reported by" className="table-sort-button" onClick={() => toggleOutOfStockOrdering('reportedBy')} type="button">Reported by {sortIcon(outOfStockSortDirection('reportedBy'))}</button></th>
          </tr></thead>
          <tbody>{outOfStockRows.map((row) => <tr key={row.id}>
            <td><strong>{row.venue_name}</strong><small>{row.venue_city}</small></td>
            <td><strong>{row.product_name}</strong><small>{row.product_sku || 'No SKU'}</small></td>
            <td><strong>{dateTime.format(new Date(row.updated_at))}</strong><small>Sales are blocked until restocked</small></td>
            <td><strong>{row.changed_by_name}</strong><small>Field update</small></td>
          </tr>)}</tbody>
        </table></div></section>
      </DataPanel>
    </> : null}
    {stockTab === 'physical' ? <>
    <div className="section-heading"><div><h2>Current supervisor physical stock</h2><p>The latest approved supervisor count for each product at each store.</p></div><span>{physicalPositions.data?.count ?? 0}</span></div>
    <DataPanel loading={physicalPositions.isLoading} error={physicalPositions.isError} empty={positionRows.length === 0}>
      <section className="content-card data-card"><div className="data-table-wrap"><table className="data-table">
        <thead><tr><th>Campaign and store</th><th>Product</th><th>Physical stock</th><th>Counted at</th><th>Supervisor</th></tr></thead>
        <tbody>{positionRows.map((row) => <tr key={`${row.campaign_location}-${row.campaign_product}`}>
          <td><strong>{row.venue_name}</strong><small>{row.campaign_name} · {row.venue_city}</small></td>
          <td><strong>{row.product_name_snapshot}</strong><small>{row.product_sku_snapshot || 'No SKU'}</small></td>
          <td><strong>{row.quantity_units.toLocaleString()} sellable units</strong><small>{Number(row.normalized_quantity).toLocaleString()} {row.normalized_unit}</small></td>
          <td><strong>{dateTime.format(new Date(row.captured_at))}</strong><small>{row.stock_count_reference}</small></td>
          <td><strong>{row.supervisor_name}</strong></td>
        </tr>)}</tbody>
      </table></div></section>
    </DataPanel>
    <div className="section-heading stock-section-heading"><div><h2>Supervisor physical count reviews</h2><p>Approve a GPS-validated count before it becomes the official physical stock position.</p></div><span>{physicalCounts.data?.count ?? 0}</span></div>
    <DataPanel loading={physicalCounts.isLoading} error={physicalCounts.isError} empty={countRows.length === 0}>
      <section className="content-card data-card"><div className="data-table-wrap"><table className="data-table">
        <thead><tr><th>Count</th><th>Campaign and store</th><th>Supervisor</th><th>Products</th><th>Status</th><th>Review</th></tr></thead>
        <tbody>{countRows.map((row) => <tr key={row.id}>
          <td><strong>{row.reference}</strong><small>{dateTime.format(new Date(row.captured_at))}</small></td>
          <td><strong>{row.venue_name}</strong><small>{row.campaign_name} · {row.venue_city}</small></td>
          <td><strong>{row.supervisor_name}</strong><small>{row.visit_reference}</small></td>
          <td><strong>{row.lines.length}</strong><small>SKU count lines</small></td>
          <td><StatusPill value={row.review_status} /></td>
          <td><button className="button secondary" onClick={() => setSelectedCount(row)} type="button"><ClipboardCheck size={15} /> Review count</button></td>
        </tr>)}</tbody>
      </table></div></section>
    </DataPanel>
    </> : null}
    {stockTab === 'receipts' ? <>
    <div className="section-heading stock-section-heading"><div><h2>Inbound receipts</h2><p>Immutable delivery entries recorded by the operating organisation.</p></div><span>{receipts.data?.count ?? 0}</span></div>
    <DataPanel loading={receipts.isLoading} error={receipts.isError} empty={rows.length === 0}>
      <section className="content-card data-card"><div className="data-table-wrap"><table className="data-table stock-table">
        <thead><tr><th>Receipt</th><th>Campaign and store</th><th>Product</th><th>Received</th><th>Delivery reference</th><th>Recorded by</th></tr></thead>
        <tbody>{rows.map((row) => <tr key={row.id}>
          <td><strong>{row.reference}</strong><small>{dateTime.format(new Date(row.received_at))}</small></td>
          <td><strong>{row.campaign_name}</strong><small>{row.venue_name} · {row.venue_city}</small></td>
          <td><strong>{row.product_name}</strong><small>{row.product_sku || 'No SKU'}</small></td>
          <td><strong>+{row.quantity_received} units</strong><small>{row.receiving_organization_name}</small></td>
          <td><strong>{row.delivery_reference || 'Not provided'}</strong><small>{row.note || 'No note'}</small></td>
          <td><strong>{row.recorded_by_name}</strong></td>
        </tr>)}</tbody>
      </table></div><div className="attendance-pagination"><span>Page {page}</span><div><button className="button secondary" disabled={!receipts.data?.previous} onClick={() => setPage((value) => Math.max(1, value - 1))}>Previous</button><button className="button secondary" disabled={!receipts.data?.next} onClick={() => setPage((value) => value + 1)}>Next</button></div></div></section>
    </DataPanel>
    </> : null}
    {selectedCount ? <div className="confirmation-backdrop" role="presentation"><section aria-label="Physical stock count detail" aria-modal="true" className="confirmation-dialog stock-count-dialog" role="dialog">
      <button aria-label="Close physical stock count" className="icon-button confirmation-close" onClick={closeCountReview} type="button"><X size={19} /></button>
      <p className="eyebrow">{selectedCount.reference}</p><h2>Physical stock count</h2>
      <div className="shelf-evidence-meta"><strong>{selectedCount.venue_name}</strong><span>{selectedCount.campaign_name} · {selectedCount.venue_city}</span><span>{selectedCount.supervisor_name} · {dateTime.format(new Date(selectedCount.captured_at))} · Visit {selectedCount.visit_reference}</span></div>
      <section className="shelf-inventory-counts"><h3>Counted products</h3>{selectedCount.lines.map((line) => <div key={line.id}><span><strong>{line.product_name_snapshot}</strong><small>{line.product_sku_snapshot || 'No SKU'}{line.product_family_snapshot ? ` · ${line.product_family_snapshot}` : ''}</small></span><span><strong>{line.quantity_units.toLocaleString()} units</strong><small>{Number(line.normalized_quantity).toLocaleString()} {line.normalized_unit}</small></span></div>)}</section>
      {selectedCount.note ? <p className="stock-count-note"><strong>Supervisor note:</strong> {selectedCount.note}</p> : null}
      {selectedCount.photos.length ? <section className="supervision-report-photos">{selectedCount.photos.map((photo) => <img alt={`Stock count evidence at ${selectedCount.venue_name}`} key={photo.id} src={photo.photo} />)}</section> : null}
      {selectedCount.review_decisions.length ? <section className="attendance-decision-history"><h3>Review history</h3>{selectedCount.review_decisions.map((item) => <article key={item.id}><StatusPill value={item.decision} /><span><strong>{item.reviewer_name}</strong><small>{dateTime.format(new Date(item.created_at))}</small></span>{item.note ? <p>{item.note}</p> : null}</article>)}</section> : null}
      {countDecision ? <label className="attendance-decision-note">Review note {countDecision === 'rejected' ? '(required)' : '(optional)'}<textarea onChange={(event) => setCountReviewNote(event.target.value)} rows={3} value={countReviewNote} /></label> : null}
      {countReviewMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(countReviewMutation.error)}</p> : null}
      <div className="confirmation-actions attendance-review-actions">
        <button className="button secondary" onClick={closeCountReview} type="button">Close</button>
        {selectedCount.can_decide && countDecision !== 'rejected' ? <button className="button danger" onClick={() => selectCountDecision('rejected')} type="button"><XCircle size={15} /> Reject</button> : null}
        {selectedCount.can_decide && countDecision !== 'approved' ? <button className="button primary" onClick={() => selectCountDecision('approved')} type="button"><CheckCircle2 size={15} /> Approve</button> : null}
        {countDecision ? <button className="button primary" disabled={countReviewMutation.isPending || (countDecision === 'rejected' && !countReviewNote.trim())} onClick={() => countReviewMutation.mutate()} type="button">{countReviewMutation.isPending ? <InlineSpinner /> : null} Confirm decision</button> : null}
      </div>
    </section></div> : null}
    {open ? <div className="confirmation-backdrop" role="presentation"><section aria-label="Receive stock" aria-modal="true" className="confirmation-dialog stock-receipt-dialog" role="dialog">
      <button aria-label="Close receive stock" className="icon-button confirmation-close" onClick={resetForm} type="button"><X size={19} /></button>
      <p className="eyebrow">Inbound delivery</p><h2>Receive stock</h2><p>Record one product delivery at one campaign store. This entry becomes part of the audit history.</p>
      <form className="management-form" onSubmit={submit}>
        <label>Campaign<select required onChange={(event) => { setCampaign(event.target.value); setLocation(''); setCampaignProduct(''); }} value={campaign}><option value="">Select campaign</option>{receivableCampaigns.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.client_name}</option>)}</select></label>
        <div className="form-grid"><label>Store<StorePicker ariaLabel="Receive stock store" disabled={!campaign} onChange={setLocation} options={campaignLocations.filter((item) => item.venue_status === 'open').map((item) => ({ id: item.id, name: item.name, address: item.address, city: item.city }))} required value={location} /></label><label>Product<select disabled={!campaign} required onChange={(event) => setCampaignProduct(event.target.value)} value={campaignProduct}><option value="">Select product</option>{campaignProducts.map((item) => <option key={item.id} value={item.id}>{item.product_name}{item.product_sku ? ` · ${item.product_sku}` : ''}</option>)}</select></label></div>
        {selectedCampaign ? <p className="form-hint">Receiving for {selectedCampaign.operating_agency_name ?? selectedCampaign.client_name}.</p> : null}
        <div className="form-grid"><label>Quantity received<input max="1000000" min="1" onChange={(event) => setQuantity(event.target.value.replace(/[^0-9]/g, ''))} required type="number" value={quantity} /></label><label>Received at<input onChange={(event) => setReceivedAt(event.target.value)} required type="datetime-local" value={receivedAt} /></label></div>
        <label>Delivery reference (optional)<input maxLength={120} onChange={(event) => setDeliveryReference(event.target.value)} placeholder="Delivery challan or invoice number" value={deliveryReference} /></label>
        <label>Note (optional)<textarea maxLength={2000} onChange={(event) => setNote(event.target.value)} rows={3} value={note} /></label>
        {mutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(mutation.error)}</p> : null}
        <div className="form-actions"><button className="button secondary" onClick={resetForm} type="button">Cancel</button><button className="button primary" disabled={mutation.isPending} type="submit">{mutation.isPending ? <InlineSpinner /> : <Boxes size={16} />} Record receipt</button></div>
      </form>
    </section></div> : null}
  </>;
}
